import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FileText, ClipboardCheck, BarChart3, Flag, Users,
  Settings2, ScrollText, LogOut, Menu, X, ShieldCheck,
} from "lucide-react";
import { PoweredByOutgrow } from "@/components/shared";
import { useBranding } from "@/context/BrandingContext";

const ROLE_LABELS = {
  tac: "TAC · Partner",
  validator: "Validator",
  am: "EnterpriseSG AM",
  pd: "PD Officer",
  admin: "Administrator",
};

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["tac", "validator", "am", "pd", "admin"] },
  { to: "/assessments", label: "My Assessments", icon: FileText, roles: ["tac"] },
  { to: "/assessments", label: "Assessments", icon: FileText, roles: ["am", "pd", "admin"] },
  { to: "/validation", label: "Validation Queue", icon: ClipboardCheck, roles: ["validator", "am", "admin"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["tac", "validator", "am", "pd", "admin"] },
  { to: "/followups", label: "Follow-ups", icon: Flag, roles: ["tac", "validator", "am", "pd", "admin"] },
  { to: "/admin/users", label: "User Management", icon: Users, roles: ["admin"] },
  { to: "/admin/config", label: "Assessment Config", icon: Settings2, roles: ["admin"] },
  { to: "/admin/audit", label: "Audit Trail", icon: ScrollText, roles: ["admin", "am", "pd"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { logo } = useBranding();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user?.role)).filter(
    (n, i, arr) => arr.findIndex((x) => x.to === n.to) === i
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 bg-[#303E49] text-slate-300 flex flex-col transition-transform lg:translate-x-0 lg:static",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          {logo ? (
            <img src={logo} alt="EnterpriseSG" className="h-9 max-w-[180px] object-contain" data-testid="brand-logo-sidebar" />
          ) : (
            <>
              <ShieldCheck className="h-6 w-6 text-brand" strokeWidth={1.75} aria-hidden="true" />
              <div className="leading-tight">
                <div className="font-heading font-extrabold text-white text-sm tracking-tight">EnterpriseSG</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Assessment Platform</div>
              </div>
            </>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to + n.label}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                  isActive
                    ? "bg-white text-slate-900"
                    : "text-slate-300 hover:bg-white/10 hover:text-white"
                )
              }
            >
              <n.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-2 py-2 mb-2">
            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mt-0.5">
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-slate-300 hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.75} aria-hidden="true" /> Sign out
          </Button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} aria-hidden="true" />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-20">
          <button
            className="lg:hidden p-2 -ml-2 text-foreground"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="overline hidden sm:block">EnterpriseSG · Partner Assessment &amp; Validation</div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-foreground leading-none">{user?.org || user?.name}</div>
              <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{user?.email}</div>
            </div>
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center font-heading font-bold text-sm">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto" data-testid="main-content">
          {children}
        </main>
        <footer className="border-t border-border bg-card px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-center" data-testid="app-footer">
          <PoweredByOutgrow />
        </footer>
      </div>
    </div>
  );
}
